import * as React from "react"
import styled, { ThemeProvider } from "styled-components"
import { theme } from "./components/theme"
import { ScreenClassProvider, setConfiguration } from "react-grid-system"
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom"
import { useCreateCart } from "./hooks/useCreateCart"
import { Breakpoints } from "./utils/design-helpers"
import OrganizationPage from "./components/templates/OrganizationPage"
import ProfilePage from "./components/templates/ProfilePage"
import StorePage from "./components/templates/StorePage"
import AboutPage from "./components/templates/AboutPage"
import HomePage from "./components/templates/HomePage"
import CheckoutPage from "./components/templates/store/CheckoutPage"
import CartPage from "./components/templates/store/CartPage"
import ConfirmationPage from "./components/templates/store/ConfirmationPage"
import LoginPage from "./components/templates/LoginPage"
import Header from "./components/organisms/Header"
import Footer from "./components/organisms/Footer"
import ContactPage from "./components/templates/ContactPage"
import PrivacyPolicy from "./components/templates/terms/PrivacyPolicy"
import DashboardPage from "./components/templates/DashboardPage"
import ActivateLoginPage from "./components/templates/login/ActivateLoginPage"
import ResetPasswordPage from "./components/templates/login/ResetPasswordPage"
import PasswordResetDonePage from "./components/templates/login/PasswordResetDonePage"
import TermsAndConditionsPage from "./components/templates/terms/TermsAndConditionsPage"
import MissingPage from "./components/templates/404Page"
import SalesTermsPage from "./components/templates/terms/SalesTermsPage"
import RentalTermsPage from "./components/templates/terms/RentalTerms"
import KuProductPage from "./components/templates/productpages/KuProductPage"
import MotionProductPage from "./components/templates/productpages/MotionProductPage"
import ProductsPage from "./components/templates/productpages/ProductsPage"
import RentalProductPage from "./components/templates/productpages/RentalProductPage"
import SecureProductPage from "./components/templates/productpages/SecureProductPage"
import CustomizeAndOrderPage from "./components/templates/store/CustomizeAndOrder/CustomizeAndOrder"
import { ArticlePageWithSlug } from "./components/templates/news/ArticlePage"
import NewsPage from "./components/templates/news/NewsPage"
import { CasePageWithParams } from "./components/templates/applications/CasePage"
import ApplicationsPage from "./components/templates/applications/ApplicationsPage"
import CampaignPage from "./components/templates/campaign/CampaignPage"
import NetworkProductPage from "./components/templates/productpages/NetworkProductPage"

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.greyLighter};
  max-width: 100vw;
  overflow-x: clip;
`

export const App = () => {
  const [cartOpen, setCartOpen] = React.useState(false)

  setConfiguration({ containerWidths: Object.values(Breakpoints) })
  const { cart, addToCart, removeFromCart, updateItemInCart, clearCart, addSolution, solutions, removeSolution } =
    useCreateCart()

  const cartQuantity = cart.reduce((acc, item) => {
    return acc + item.amount
  }, 0)

  function ScrollToTop() {
    const { pathname } = useLocation()

    React.useMemo(() => {
      if (!pathname?.includes("store") || pathname.includes("checkout") || pathname.includes("customize")) {
        window.scrollTo(0, 0)
      }
    }, [pathname])

    return null
  }

  //also add URLs to urls.py
  return (
    <ThemeProvider theme={theme}>
      <Wrapper>
        <ScreenClassProvider>
          <BrowserRouter>
            <Header
              cartNumber={cartQuantity}
              addToCart={addToCart}
              removeFromCart={removeFromCart}
              updateItemInCart={updateItemInCart}
              clearCart={clearCart}
              cart={cart}
              solutions={solutions}
              removeSolution={removeSolution}
              cartOpen={cartOpen}
              setCartOpen={setCartOpen}
            />
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/dashboard/:page" element={<DashboardPage />} />
              <Route path="/dashboard/terminals" element={<DashboardPage />} />
              <Route path="/dashboard/terminals/:page" element={<DashboardPage />} />
              <Route path="/dashboard/groups" element={<DashboardPage />} />
              <Route path="/dashboard/groups/:page" element={<DashboardPage />} />
              <Route path="/organization" element={<OrganizationPage />} />
              <Route path="/organization/:slug" element={<OrganizationPage />} />
              <Route path="/organization/:slug/:page" element={<OrganizationPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/:slug" element={<ProfilePage />} />
              <Route path="/profile/:slug/two_factor" element={<ProfilePage />} />
              <Route path="/profile/:slug/two_factor/:secondSlug" element={<ProfilePage />} />
              <Route path="/profile/:slug/two_factor/:secondSlug/:thirdSlug" element={<ProfilePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/applications" element={<ApplicationsPage />} />
              <Route path="/applications/:slug" element={<CasePageWithParams />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<ArticlePageWithSlug />} />
              <Route path="/campaign/:slug" element={<CampaignPage />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/store" element={<StorePage addToCart={addToCart} />} />
              <Route
                path="/store/cart"
                element={
                  <CartPage
                    addToCart={addToCart}
                    removeFromCart={removeFromCart}
                    updateItemInCart={updateItemInCart}
                    cart={cart}
                    solutions={solutions}
                    removeSolution={removeSolution}
                  />
                }
              />
              <Route
                path="/store/checkout"
                element={
                  <CheckoutPage
                    onClearCart={clearCart}
                    addToCart={addToCart}
                    removeFromCart={removeFromCart}
                    updateItemInCart={updateItemInCart}
                    cart={cart}
                    solutions={solutions}
                  />
                }
              />
              <Route path="/store/confirmation" element={<ConfirmationPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<LoginPage />} />

              <Route path="/privacy_policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
              <Route path="/sales-terms" element={<SalesTermsPage />} />
              <Route path="/rental-terms" element={<RentalTermsPage />} />

              <Route path="/activate" element={<ActivateLoginPage />} />
              <Route path="/activate/:uuid/:key" element={<ActivateLoginPage />} />

              <Route path="/reset/done" element={<PasswordResetDonePage />} />

              <Route path="/reset/:slug/set-password/" element={<ResetPasswordPage />} />

              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/ku" element={<KuProductPage />} />
              <Route path="/products/secure" element={<SecureProductPage />} />
              <Route path="/products/rental" element={<RentalProductPage />} />
              <Route path="/products/motion" element={<MotionProductPage />} />
              <Route path="/products/network" element={<NetworkProductPage />} />

              <Route
                path="/store/customize/ku"
                element={
                  <CustomizeAndOrderPage
                    terminalType="ku"
                    addToCart={addToCart}
                    removeFromCart={removeFromCart}
                    addSolution={addSolution}
                    removeSolution={removeSolution}
                    setCartOpen={setCartOpen}
                  />
                }
              />
              <Route
                path="/store/customize/secure"
                element={
                  <CustomizeAndOrderPage
                    terminalType="secure"
                    addToCart={addToCart}
                    removeFromCart={removeFromCart}
                    addSolution={addSolution}
                    removeSolution={removeSolution}
                    setCartOpen={setCartOpen}
                  />
                }
              />

              <Route path="*" element={<MissingPage />} />
            </Routes>

            <Footer />
          </BrowserRouter>
        </ScreenClassProvider>

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet" />
      </Wrapper>
    </ThemeProvider>
  )
}
